import { Box, Divider, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import useInfiniteScrollFetcher from "../../hooks/useInfiniteScrollFetcher";
import { ReportContainerPropsType } from "../../interface/account";
import { apiGetAccountPetitions, apiGetAccountPosts } from "../../api/account";
import ReportContainer from "../report-container";
import { SkeletonAccountPostLoading } from "../ui-elements/skeleton-loading";
import { PostContentDetails } from "../../interface/posts";
import { AccountReportContent } from "../account/account-report-content";

const RepresentativeActivity = ({ reps_id }: { reps_id: number }) => {
  const paths = ["Post", "Petition"];
  const [path_name, setPath_name] = useState("Post");
  const handle_path_name = (path_name: string) => setPath_name(path_name);

  return (
    <Box>
      <Stack
        direction={"row"}
        justifyContent={"space-around"}
        sx={{
          backgroundColor: "#E2F1EB00",
          borderTop: "1px solid #E7EBEF",
          boxShadow: `0px 2px 7px 0px #45917326`,
          borderRadius: "8px",
          my: 2,
          p: 2,
          overflowX: "auto",
        }}
      >
        {paths?.map((path, index) => (
          <Stack
            key={index}
            sx={{
              borderBottom: path_name === path ? "2px solid #459173" : "",
              mx: 1,
              px: 1,
              cursor: "pointer",
            }}
            onClick={() => handle_path_name(path)}
          >
            <Typography
              sx={{
                fontWeight: path_name === path ? 500 : 400,
                color: path_name === path ? "#459173" : "#637D92",
                whiteSpace: "nowrap",
              }}
            >
              {path}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {path_name === "Petition" && <RepsAccountPetition reps_id={reps_id} />}
      {path_name === "Post" && <RepsAccountPosts reps_id={reps_id} />}
    </Box>
  );
};

export default RepresentativeActivity;

const RepsAccountPetition = ({ reps_id }: { reps_id: number }) => {
  const { items, sentryRef, loading } =
    useInfiniteScrollFetcher<ReportContainerPropsType>({
      fetchData: apiGetAccountPetitions,
      initialParams: { page: 1, page_size: 10, account_id: reps_id },
    });

  return (
    <Box>
      {items?.map((feed: ReportContainerPropsType, index: number) => (
        <Box key={index}>
          <ReportContainer
            key={index}
            id={feed?.id}
            link={feed?.id}
            content={feed?.context}
            account={feed?.author}
            title={feed?.title}
            source={feed?.post_type}
            badge={feed?.author_badge ?? 0}
            avatarImg={feed?.author_photo_url ?? ""}
            date={feed?.created_at}
            image={feed?.media}
            current_user_bookmarked={feed?.current_user_bookmarked ?? false}
            current_user_liked={feed?.current_user_liked ?? false}
            current_user_reposted={feed?.current_user_reposted ?? false}
            data={feed}
            social={{
              message: feed?.comments,
              repost: feed?.reposts,
              like: feed?.likes,
            }}
          />
          <Divider sx={{ my: 3 }} />
        </Box>
      ))}
      <div ref={sentryRef} style={{ width: "560px", margin: "0 auto" }}>
        {loading && (
          <Box sx={{}}>
            <SkeletonAccountPostLoading />
          </Box>
        )}
      </div>
      {!loading && items?.length === 0 && (
        <Box
          sx={{
            width: "80%",
            mx: "auto",
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            No Petition has been created.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const RepsAccountPosts = ({ reps_id }: { reps_id: number }) => {
  const { items, sentryRef, loading } =
    useInfiniteScrollFetcher<PostContentDetails>({
      fetchData: apiGetAccountPosts,
      initialParams: { page: 1, page_size: 10, account_id: reps_id },
    });

  return (
    <Box>
      {items?.map((post: any, index: number) => (
        <Box key={index}>
          <AccountReportContent post_details={post} />
          <Divider sx={{ my: 3 }} />
        </Box>
      ))}
      <div ref={sentryRef} style={{ width: "560px", margin: "0 auto" }}>
        {loading && (
          <Box sx={{}}>
            <SkeletonAccountPostLoading />
          </Box>
        )}
      </div>
      {!loading && items?.length === 0 && (
        <Box
          sx={{
            width: "80%",
            mx: "auto",
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            No Posts has been made.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
